(function (root) {
  'use strict';
  const sizes = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL'];
  function measurement(value, unit = 'cm') {
    if (value && typeof value === 'object') return measurement(value.value, value.unit);
    const match = String(value ?? '').trim().match(/^(\d+(?:[.,]\d+)?)\s*(cm|mm|m)?$/i);
    if (!match) return null;
    const factor = { cm: 1, mm: 0.1, m: 100 }[(match[2] || unit || 'cm').toLowerCase()];
    const number = Number(match[1].replace(',', '.')) * factor;
    return Number.isFinite(number) && number > 0 ? Math.round(number * 100) / 100 : null;
  }
  function sizeRange(value) {
    const normalized = String(value ?? '').toUpperCase().trim().replace(/2XL/g, 'XXL').replace(/2XS/g, 'XXS');
    if (!/^(XXS|XS|S|M|L|XL|XXL)(\s*[\/–-]\s*(XXS|XS|S|M|L|XL|XXL))?$/.test(normalized)) return null;
    const indexes = normalized.split(/\s*[\/–-]\s*/).map(size => sizes.indexOf(size));
    return [Math.min(...indexes), Math.max(...indexes)];
  }
  function profile(value) {
    if (!value || typeof value !== 'object') return null;
    const height = measurement(value.height);
    if (height === null || height < 80 || height > 250 || !sizes.includes(value.size)) return null;
    if (!['', 'slim', 'balanced', 'broad'].includes(value.body)) return null;
    if (!['fitted', 'regular', 'relaxed', 'oversize'].includes(value.fit)) return null;
    const gender = value.gender ?? '';
    if (!['', 'woman', 'man'].includes(gender)) return null;
    if (typeof value.store !== 'string' || value.store.length > 40) return null;
    const width = value.width == null || value.width === '' ? null : measurement(value.width);
    const length = value.length == null || value.length === '' ? null : measurement(value.length);
    if (value.width != null && value.width !== '' && (width === null || width < 20 || width > 120)) return null;
    if (value.length != null && value.length !== '' && (length === null || length < 20 || length > 150)) return null;
    return { gender, height, size: value.size, body: value.body, fit: value.fit === 'relaxed' ? 'oversize' : value.fit, store: value.store.trim(), width, length };
  }
  function fitBands(dimension, fit, reference) {
    const presets = {
      fitted: { near: [2.5, 0.05], narrower: [5, 0.10], wider: [6, 0.12] },
      regular: { near: [3, 0.06], narrower: [6, 0.12], wider: [8, 0.16] },
      oversize: { near: [4, 0.08], narrower: [8, 0.15], wider: [12, 0.22] }
    };
    const p = dimension === 'length' ? { near: [4, 0.06], narrower: [8, 0.12], wider: [8, 0.12] } : presets[fit] || presets.regular;
    const bound = pair => Math.round(Math.max(pair[0], reference * pair[1]) * 100) / 100;
    return { near: bound(p.near), narrower: bound(p.narrower), wider: bound(p.wider) };
  }
  function garmentMeasurement(product, dimension) {
    const value = measurement(product[dimension], product[dimension + 'Unit']);
    return value !== null && value >= 20 && value <= (dimension === 'width' ? 120 : 150) ? value : null;
  }
  function evaluate(product, user) {
    const result = { mode: 'quick', size: 'unknown', width: null, length: null, missing: [] };
    const actualSize = sizeRange(product.size);
    const preferred = sizeRange(user.size);
    if (actualSize && preferred) result.size = actualSize[1] < preferred[0] ? 'smaller' : actualSize[0] > preferred[1] ? 'larger' : 'similar';
    for (const dimension of ['width', 'length']) {
      const actual = garmentMeasurement(product, dimension);
      if (actual === null) result.missing.push(dimension);
      if (actual !== null && user[dimension] !== null && user[dimension] !== undefined) {
        const reference = measurement(user[dimension]);
        if (reference === null) continue;
        const difference = Math.round((actual - reference) * 100) / 100;
        const bands = fitBands(dimension, user.fit, reference);
        result[dimension] = { actual, reference, difference, similar: Math.abs(difference) <= bands.near, bands };
        result.mode = 'measured';
      }
    }
    return result;
  }
  function axisFit(dimension, comparison) {
    if (!comparison) return { code: 'unknown', source: 'unknown' };
    const { difference, bands } = comparison;
    let code = 'similar';
    if (difference < -bands.narrower) code = dimension === 'width' ? 'much_closer' : 'much_shorter';
    else if (difference > bands.wider) code = dimension === 'width' ? 'much_looser' : 'much_longer';
    else if (difference < -bands.near) code = dimension === 'width' ? 'closer' : 'shorter';
    else if (difference > bands.near) code = dimension === 'width' ? 'looser' : 'longer';
    return { code, source: 'measurement', ...comparison };
  }
  function sizeFit(product, user) {
    const actual = sizeRange(product.size), preferred = sizeRange(user.size);
    if (!actual || !preferred) return { code: 'unknown', source: 'unknown' };
    const gap = actual[0] > preferred[1] ? actual[0] - preferred[1] : actual[1] < preferred[0] ? actual[1] - preferred[0] : 0;
    const largerAllowance = user.fit === 'oversize' ? 2 : 1;
    return { code: gap === 0 ? 'similar' : gap < -1 ? 'much_closer' : gap < 0 ? 'closer' : gap > largerAllowance ? 'much_looser' : 'looser', source: 'size', gap };
  }
  function assessFit(product, user) {
    const result = evaluate(product, user);
    const width = result.width ? axisFit('width', result.width) : sizeFit(product, user);
    const length = axisFit('length', result.length);
    const complete = Boolean(result.width && result.length);
    const needs = !result.width && user.width == null && garmentMeasurement(product, 'width') !== null ? 'width' : !result.length && user.length == null && garmentMeasurement(product, 'length') !== null ? 'length' : null;
    let level = 'possible', code = 'quick_match';
    if (width.code === 'much_closer') { level = 'check'; code = 'may_tight'; }
    else if (width.code === 'much_looser') { level = 'check'; code = 'much_looser'; }
    else if (length.code === 'much_shorter') { level = 'check'; code = 'much_shorter'; }
    else if (length.code === 'much_longer') { level = 'check'; code = 'much_longer'; }
    else if (width.code === 'looser') { level = 'conditional'; code = 'looser'; }
    else if (width.code === 'closer') { level = 'conditional'; code = 'closer'; }
    else if (length.code === 'shorter') { level = 'conditional'; code = complete ? 'fits_shorter' : 'shorter'; }
    else if (length.code === 'longer') { level = 'conditional'; code = complete ? 'fits_longer' : 'longer'; }
    else if (complete) { level = 'yes'; code = 'fits'; }
    else if (result.width) code = 'partial_width';
    else if (result.length) code = 'partial_length';
    else if (width.code === 'unknown' || user.width != null || user.length != null) { level = 'check'; code = 'insufficient'; }
    return { level, code, width, length, needs, mode: result.mode, complete, comparison: result };
  }
  function suggestions(catalog, user, current) {
    if (!Array.isArray(catalog)) return { mode: 'quick', items: [] };
    const dimensions = ['width', 'length'].filter(key => user[key] != null);
    const measured = dimensions.length > 0;
    const range = sizeRange(user.size);
    const brand = String(user.store || '').toLowerCase().trim();
    const score = item => {
      const values = dimensions.map(key => garmentMeasurement(item, key));
      if (values.some(value => value === null)) return Infinity;
      return values.reduce((sum, value, index) => {
        const key = dimensions[index], difference = value - user[key], bands = fitBands(key, user.fit, user[key]);
        const beyond = difference < -bands.narrower || difference > bands.wider;
        return sum + Math.abs(difference) / bands.near + (beyond ? 3 : 0);
      }, 0);
    };
    const currentScore = measured && current ? score(current) : Infinity;
    let candidates = catalog.filter(item => {
      if (!item || item.available === false || item.handle === current?.handle || !/^[a-z0-9][a-z0-9-]*$/.test(item.handle || '')) return false;
      if (measured) return score(item) + 0.15 < currentScore;
      const actual = sizeRange(item.size);
      return actual && range && actual[0] <= range[1] && actual[1] >= range[0];
    });
    const sameBrand = item => String(item.vendor || '').toLowerCase().trim() === brand;
    if (!measured && brand && candidates.some(sameBrand)) candidates = candidates.filter(sameBrand);
    candidates.sort((a, b) => measured ? score(a) - score(b) || Number(sameBrand(b)) - Number(sameBrand(a)) : Number(sameBrand(b)) - Number(sameBrand(a)));
    return { mode: measured ? 'measured' : 'quick', items: candidates.slice(0, 2) };
  }
  function advice(product, user) {
    const { level, code } = assessFit(product, user);
    return { level, code };
  }
  const rules = { measurement, sizeRange, profile, evaluate, suggestions, advice, assessFit, fitBands, garmentMeasurement };
  if (typeof module !== 'undefined' && module.exports) module.exports = rules;
  else root.VSFitRules = rules;
})(globalThis);

(function () {
  'use strict';
  if (typeof document === 'undefined' || window.VSFitSession) return;
  const rules = window.VSFitRules;
  const config = document.getElementById('VSFitMessages');
  const dialog = document.getElementById('VSFitDialog');
  if (!rules || !config || !dialog || typeof dialog.showModal !== 'function') return;
  let messages;
  try { messages = JSON.parse(config.dataset.fitMessages); } catch (_) { return; }
  let catalog = [];
  let catalogRequest = null;
  const form = dialog.querySelector('form');
  const key = 'vs_fit_profile_v1';
  const tabId = typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2);
  let state = { v: 1, at: 0, id: '', profile: null };
  let storageAvailable = true;
  let channel = null;
  let opener = null;
  let productContext = null;
  let previousOverflow = '';
  let rendering = false;
  let currentStep = 1;
  let measurementStart = 8;
  const t = (name, values = {}) => String(messages[name] || '').replace(/\[\[(\w+)\]\]/g, (_, parameter) => String(values[parameter] ?? ''));
  const format = number => new Intl.NumberFormat(document.documentElement.lang || 'pl', { maximumFractionDigits: 2 }).format(number);
  function envelope(value) {
    if (!value || value.v !== 1 || !Number.isSafeInteger(value.at) || value.at < 0 || value.at > Date.now() + 60000 || typeof value.id !== 'string' || value.id.length > 100) return null;
    const cleanProfile = value.profile === null ? null : rules.profile(value.profile);
    if (value.profile !== null && !cleanProfile) return null;
    return { v: 1, at: value.at, id: value.id, profile: cleanProfile };
  }
  function newer(candidate) { return candidate.at > state.at || (candidate.at === state.at && candidate.id > state.id); }
  function persist() {
    try { sessionStorage.setItem(key, JSON.stringify(state)); } catch (_) { storageAvailable = false; }
  }
  function restore() {
    let raw;
    try {
      raw = sessionStorage.getItem(key);
    } catch (_) { storageAvailable = false; }
    try {
      const saved = envelope(JSON.parse(raw));
      if (saved && newer(saved)) state = saved;
    } catch (_) {}
  }
  function post(message) {
    if (!channel) return;
    try { channel.postMessage(message); } catch (_) { channel = null; render(); }
  }
  function adopt(value) {
    const candidate = envelope(value);
    if (!candidate || !newer(candidate)) return;
    state = candidate;
    persist();
    render();
  }
  function commit(value) {
    state = { v: 1, at: Math.max(Date.now(), state.at + 1), id: tabId, profile: value };
    persist();
    post({ kind: 'state', state });
    render();
  }
  function productFrom(card) {
    if (!card) return null;
    return { handle: card.dataset.fitHandle, size: card.dataset.fitSize, width: card.dataset.fitWidth, widthUnit: card.dataset.fitWidthUnit, length: card.dataset.fitLength, lengthUnit: card.dataset.fitLengthUnit };
  }
  function view(product, user) {
    if (!product) return { title: t('profileReady'), summary: t('profileHint'), detail: '', note: '', needs: null, hasReference: user.width != null || user.length != null };
    const decision = rules.assessFit(product, user);
    const result = decision.comparison;
    const title = t('assessment_' + decision.code);
    const summary = [t((decision.width.source === 'size' ? 'width_size_' : 'width_fit_') + decision.width.code), t('length_fit_' + decision.length.code)].join(' ');
    const parts = [];
    const itemSize = rules.sizeRange(product.size) ? String(product.size).trim() : '—';
    parts.push(t('quick_sizes', { item: itemSize, usual: user.size }));
    for (const dimension of ['width', 'length']) {
      const comparison = result[dimension];
      if (comparison) {
        const direction = comparison.difference === 0 ? 'same' : comparison.difference > 0 ? 'more' : 'less';
        parts.push(t(dimension + '_' + direction, { amount: format(Math.abs(comparison.difference)), actual: format(comparison.actual), reference: format(comparison.reference) }));
      } else {
        const actual = rules.garmentMeasurement(product, dimension);
        if (actual !== null) parts.push(t('actual_' + dimension, { amount: format(actual) }));
      }
    }
    if (result.missing.length) parts.push(t('missing', { fields: result.missing.map(name => t('dimension_' + name)).join(', ') }));
    const note = t('preference', { fit: t('fit_' + user.fit) }) + ' ' + t(decision.complete ? 'measuredBasis' : decision.mode === 'measured' ? 'partial_basis' : 'quickHint');
    return { title, summary, detail: parts.join(' '), note, compact: title, level: decision.level, needs: decision.needs, hasReference: user.width != null || user.length != null };
  }
  function memoryText() { return !storageAvailable ? t('memoryPage') : !channel ? t('memoryTab') : t('memory'); }
  function render() {
    if (rendering) return;
    rendering = true;
    document.querySelectorAll('[data-fit-open]').forEach(button => { if (button.disabled) button.disabled = false; });
    document.querySelectorAll('[data-fit-card]').forEach(card => {
      const active = Boolean(state.profile);
      const button = card.querySelector('[data-fit-open]');
      const summary = card.querySelector('[data-fit-summary]');
      const detail = card.querySelector('[data-fit-detail]');
      const result = active ? view(productFrom(card), state.profile) : { title: t('intro'), detail: '' };
      const label = button?.querySelector('[data-fit-button-label]') || button;
      if (label) label.textContent = t(active ? 'edit' : 'open').replace(/\s*→$/, '');
      if (summary) {
        summary.textContent = active ? result.compact : '';
        summary.hidden = !active;
      }
      if (detail) {
        if (detail.textContent !== result.detail) detail.textContent = result.detail;
        detail.hidden = !active;
      }
      card.classList.toggle('vs-fit-card--active', active);
    });
    document.querySelectorAll('[data-fit-entry]').forEach(entry => {
      const button = entry.querySelector('[data-fit-open]');
      const label = t(state.profile ? 'collectionActive' : 'collectionOpen');
      if (button && button.textContent !== label) button.textContent = label;
    });
    const memory = dialog.querySelector('[data-fit-memory]');
    if (memory && memory.textContent !== memoryText()) memory.textContent = memoryText();
    dialog.querySelector('[data-fit-clear]').hidden = !state.profile;
    rendering = false;
  }
  function field(name) { return form.elements.namedItem(name); }
  function draft() {
    return { gender: field('gender').value, height: field('height').value, size: field('size').value, body: field('body').value, fit: field('fit').value, store: field('store').value, width: field('width').value, length: field('length').value };
  }
  function error(name, message) {
    let input = field(name);
    if (!input || typeof input.focus !== 'function') input = form.querySelector('[name="' + name + '"]');
    const panel = input?.closest('[data-fit-step]');
    if (panel && panel.hidden) step(Number(panel.dataset.fitStep));
    const box = dialog.querySelector('[data-fit-error]');
    box.textContent = t(message);
    box.hidden = false;
    if (input && typeof input.focus === 'function') {
      const disclosure = input.closest('details');
      if (disclosure) disclosure.open = true;
      input.setAttribute('aria-invalid', 'true');
      input.focus();
    }
    return null;
  }
  function clearErrors() {
    dialog.querySelector('[data-fit-error]').hidden = true;
    form.querySelectorAll('[aria-invalid]').forEach(input => input.removeAttribute('aria-invalid'));
  }
  function validate(includeMeasurements) {
    clearErrors();
    const value = draft();
    const height = rules.measurement(value.height);
    if (height === null || height < 80 || height > 250) return error('height', 'heightError');
    if (!rules.sizeRange(value.size)) return error('size', 'sizeError');
    if (includeMeasurements) {
      for (const [name, max] of [['width', 120], ['length', 150]]) {
        const number = rules.measurement(value[name]);
        if (value[name].trim() && (number === null || number < 20 || number > max)) return error(name, name + 'Error');
      }
    } else { value.width = null; value.length = null; }
    return rules.profile(value);
  }
  function preview() {
    const raw = draft();
    raw.width = rules.measurement(raw.width);
    raw.length = rules.measurement(raw.length);
    const user = rules.profile(raw);
    if (!user) return;
    const result = view(productContext, user);
    dialog.querySelector('[data-fit-preview-title]').textContent = result.title;
    dialog.querySelector('.vs-fit-dialog__result').dataset.fitVerdict = result.level || 'check';
    dialog.querySelector('[data-fit-preview-summary]').textContent = result.summary;
    dialog.querySelector('[data-fit-preview-detail]').textContent = result.detail;
    const resultDetails = dialog.querySelector('[data-fit-result-details]');
    resultDetails.hidden = !result.detail;
    const measureButton = dialog.querySelector('[data-fit-measure]');
    measureButton.textContent = t(result.needs === 'length' ? 'measure_length_next' : result.needs === 'width' ? 'measure_width_next' : result.hasReference ? 'measure_edit' : 'measure_toggle') + ' →';
    measurementStart = result.needs === 'length' ? 9 : 8;
    dialog.querySelector('[data-fit-preview-note]').textContent = result.note;
    const suggestion = rules.suggestions(catalog, user, productContext);
    const alternatives = dialog.querySelector('[data-fit-alternatives]');
    const list = dialog.querySelector('[data-fit-alternative-list]');
    if (alternatives && list) {
      alternatives.hidden = !suggestion.items.length;
      alternatives.querySelector('summary').textContent = t(suggestion.mode === 'measured' ? 'alternativesMeasured' : 'alternativesQuick');
      list.replaceChildren();
      suggestion.items.forEach(item => {
        const link = document.createElement('a');
        link.className = 'vs-fit-alternative';
        link.href = '/products/' + item.handle;
        const title = document.createElement('strong');
        title.textContent = item.title;
        const details = document.createElement('span');
        const width = rules.measurement(item.width);
        const length = rules.measurement(item.length);
        details.textContent = length && width ? format(length) + ' × ' + format(width) + ' cm · ' + item.size : item.size || '';
        link.append(title, details);
        list.append(link);
      });
    }
  }
  function syncBrandChoice() {
    const store = field('store').value;
    const inputs = Array.from(form.querySelectorAll('[name="brand"]'));
    const knownValue = inputs.find(input => input.value.toLowerCase() === store.toLowerCase().trim() && input.value !== 'other')?.value;
    const known = Boolean(knownValue);
    inputs.forEach(input => { input.checked = known ? input.value === knownValue : Boolean(store) && input.value === 'other'; });
    dialog.querySelector('[data-fit-other-brand]').hidden = known || !store;
  }
  function loadCatalog() {
    if (catalogRequest || !config.dataset.fitCatalogUrl) return;
    catalogRequest = fetch(config.dataset.fitCatalogUrl, { credentials: 'same-origin' })
      .then(response => { if (!response.ok) throw new Error('catalog'); return response.text(); })
      .then(html => {
        const source = new DOMParser().parseFromString(html, 'text/html').getElementById('VSFitCatalog');
        const items = JSON.parse(source?.dataset.fitCatalog || '[]');
        if (Array.isArray(items)) catalog = items;
        if (dialog.open && currentStep === 7) preview();
      })
      .catch(() => { catalogRequest = null; });
  }
  function step(number) {
    currentStep = number;
    dialog.querySelectorAll('[data-fit-step]').forEach(panel => { panel.hidden = Number(panel.dataset.fitStep) !== number; });
    dialog.querySelector('[data-fit-progress]').textContent = number === 7 ? t('resultProgress') : number > 7 ? t('measurementStep', { number: number - 7 }) : t('step', { number });
    dialog.querySelector('[data-fit-back]').hidden = number === 1;
    dialog.querySelector('[data-fit-next]').hidden = number === 7;
    dialog.querySelector('[data-fit-save]').hidden = number !== 7;
    dialog.querySelector('[data-fit-next]').textContent = t(number === 9 ? 'showComparison' : 'next');
    dialog.querySelector('[data-fit-skip]').hidden = ![1, 3, 5, 8, 9].includes(number);
    dialog.dataset.fitGender = field('gender').value || 'man';
    clearErrors();
    if (number === 7) { dialog.querySelector('[data-fit-result-details]').open = false; preview(); }
    const heading = dialog.querySelector('[data-fit-step="' + number + '"] h2');
    heading.focus();
  }
  function next() {
    clearErrors();
    const value = draft();
    if (currentStep === 2) {
      const height = rules.measurement(value.height);
      if (height === null || height < 80 || height > 250) return error('height', 'heightError');
    }
    if (currentStep === 4 && !rules.sizeRange(value.size)) return error('size', 'sizeError');
    if (currentStep === 8 || currentStep === 9) {
      const name = currentStep === 8 ? 'width' : 'length';
      const amount = rules.measurement(value[name]);
      if (value[name].trim() && (amount === null || amount < 20 || amount > (name === 'width' ? 120 : 150))) return error(name, name + 'Error');
    }
    step(currentStep === 9 ? 7 : currentStep + 1);
  }
  function close() {
    if (!dialog.open) return;
    dialog.close();
    document.documentElement.removeAttribute('data-vs-fit-open');
    document.documentElement.style.overflow = previousOverflow;
    if (opener) {
      opener.setAttribute('aria-expanded', 'false');
      if (opener.isConnected) opener.focus({ preventScroll: true });
    }
    document.dispatchEvent(new CustomEvent('vs:fit-close'));
  }
  function open(button) {
    if (dialog.open) return;
    opener = button;
    productContext = productFrom(button.closest('[data-fit-card]') || document.querySelector('[data-fit-card]'));
    form.reset();
    const saved = state.profile;
    if (saved) {
      for (const [name, value] of Object.entries(saved)) field(name).value = value === null ? '' : String(value);
    }
    syncBrandChoice();
    const alternatives = dialog.querySelector('[data-fit-alternatives]');
    if (alternatives) alternatives.open = false;
    dialog.querySelector('[data-fit-step="7"] h2').textContent = t(productContext ? 'step2Product' : 'step2Profile');
    dialog.querySelector('[data-fit-save]').textContent = t(productContext ? 'saveProduct' : 'saveProfile');
    previousOverflow = document.documentElement.style.overflow;
    document.documentElement.setAttribute('data-vs-fit-open', '');
    document.documentElement.style.overflow = 'hidden';
    dialog.showModal();
    loadCatalog();
    button.setAttribute('aria-expanded', 'true');
    step(1);
    render();
  }
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-fit-open]');
    if (button) { event.preventDefault(); open(button); }
  });
  dialog.querySelector('[data-fit-close]').addEventListener('click', close);
  dialog.addEventListener('cancel', event => { event.preventDefault(); close(); });
  dialog.addEventListener('click', event => {
    if (event.target !== dialog) return;
    const bounds = dialog.getBoundingClientRect();
    if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) close();
  });
  dialog.querySelector('[data-fit-next]').addEventListener('click', next);
  dialog.querySelector('[data-fit-back]').addEventListener('click', () => step(currentStep === 8 || (currentStep === 9 && measurementStart === 9) ? 7 : Math.max(1, currentStep - 1)));
  dialog.querySelector('[data-fit-measure]').addEventListener('click', () => step(measurementStart));
  dialog.querySelector('[data-fit-skip]').addEventListener('click', () => {
    const names = { 1: 'gender', 3: 'body', 5: 'store', 8: 'width', 9: 'length' };
    if (names[currentStep]) {
      form.querySelectorAll('[name="' + names[currentStep] + '"]').forEach(input => {
        if (input.type === 'radio') input.checked = false;
        else input.value = '';
      });
    }
    if (currentStep === 5) syncBrandChoice();
    next();
  });
  form.addEventListener('submit', event => {
    event.preventDefault();
    if (currentStep !== 7) {
      next();
      return;
    }
    const user = validate(true);
    if (user) { commit(user); close(); }
  });
  form.addEventListener('input', () => { if (currentStep === 7) preview(); });
  form.addEventListener('change', event => {
    if (event.target.name !== 'brand') return;
    const selected = event.target.value;
    const other = selected === 'other';
    const known = Array.from(form.querySelectorAll('[name="brand"]')).some(input => input.value.toLowerCase() === field('store').value.toLowerCase().trim());
    if (!other) field('store').value = selected;
    else if (known) field('store').value = '';
    dialog.querySelector('[data-fit-other-brand]').hidden = !other;
    if (other) field('store').focus();
  });
  dialog.querySelector('[data-fit-clear]').addEventListener('click', () => { commit(null); close(); });
  restore();
  persist();
  try {
    channel = new BroadcastChannel('vs_fit_session_v1');
    channel.onmessage = event => {
      if (event.data?.kind === 'request') post({ kind: 'state', state });
      else if (event.data?.kind === 'state') adopt(event.data.state);
    };
  } catch (_) { channel = null; }
  window.VSFitSession = { initialized: true };
  render();
  post({ kind: 'request' });
  window.addEventListener('pageshow', () => { restore(); render(); post({ kind: 'request' }); });
  document.addEventListener('shopify:section:load', render);
  const observer = new MutationObserver(records => {
    if (records.some(record => Array.from(record.addedNodes).some(node => node.nodeType === 1 && (node.matches('[data-fit-card],[data-fit-entry],[data-fit-open]') || node.querySelector('[data-fit-card],[data-fit-entry],[data-fit-open]'))))) render();
  });
  observer.observe(document.getElementById('MainContent') || document.body, { childList: true, subtree: true });
})();


/* Jeans fit: source-backed comparisons, isolated from the sweatshirt rules. */
(function (root) {
  'use strict';
  const axes = ['waist', 'hips', 'thigh', 'inseam'];
  function cm(raw) {
    if (raw && typeof raw === 'object') return cm(String(raw.value) + ' ' + (raw.unit || 'cm'));
    const match = String(raw ?? '').trim().match(/^(\d+(?:[.,]\d+)?)\s*(cm|mm|m)?$/i);
    if (!match) return null;
    const n = Number(match[1].replace(',', '.')) * ({ cm: 1, mm: .1, m: 100 }[(match[2] || 'cm').toLowerCase()]);
    return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : null;
  }
  function validAxis(value, axis, kind) {
    const n = cm(value);
    const bounds = axis === 'inseam' ? [20, 150] : kind === 'body' ? [20, 240] : [10, 120];
    return n !== null && n >= bounds[0] && n <= bounds[1] ? n : null;
  }
  function cleanProfile(value) {
    if (!value || typeof value !== 'object') return null;
    if (!['women', 'men'].includes(value.category) || !['zara','hm','levis','other'].includes(value.brand)) return null;
    if (!['EU', 'W', 'alpha'].includes(value.sizeType) || !/^[A-Z0-9]{1,6}$/.test(String(value.size || ''))) return null;
    const height = cm(value.height);
    if (height === null || height < 130 || height > 220) return null;
    if (!['unknown','low','mid','high'].includes(value.referenceRise) || !['unknown','slim','straight','wide','mom','bootcut'].includes(value.referenceFit)) return null;
    if (!['less','standard','more','unknown'].includes(value.hipsRoom) || !['less','standard','more','unknown'].includes(value.thighRoom)) return null;
    if (!['fitted','regular','relaxed'].includes(value.preference)) return null;
    const leg = value.leg === '' || value.leg == null ? null : Number(value.leg);
    if (leg !== null && (!Number.isInteger(leg) || leg < 24 || leg > 40)) return null;
    if (!['garment','body'].includes(value.measurementMode || 'garment')) return null;
    const measurements = {};
    for (const axis of axes) {
      const raw = value.measurements?.[axis];
      measurements[axis] = raw == null || raw === '' ? null : validAxis(raw, axis, value.measurementMode || 'garment');
      if (raw != null && raw !== '' && measurements[axis] === null) return null;
    }
    return {category:value.category,brand:value.brand,sizeType:value.sizeType,size:String(value.size),model:typeof value.model==='string'?value.model.slice(0,64):'',height,leg,referenceRise:value.referenceRise,referenceFit:value.referenceFit,hipsRoom:value.hipsRoom,thighRoom:value.thighRoom,preference:value.preference,measurementMode:value.measurementMode||'garment',measurements};
  }
  function findReference(data, profile) {
    const candidates = data.charts.filter(c=>c.brand===profile.brand && c.category===profile.category && c.sizeType===profile.sizeType);
    const chart = profile.model ? candidates.find(c=>c.id===profile.model) : candidates.find(c=>c.generic);
    if (!chart || !chart.rows[profile.size]) return null;
    return {chart,row:chart.rows[profile.size]};
  }
  function compare(product, profile, data) {
    const reference = findReference(data, profile);
    const actual = Object.fromEntries(axes.map(axis=>[axis,validAxis(product[axis],axis,'garment')]));
    const actualRise = cm(product.rise);
    const stretch=String(product.stretch||'').trim().toLowerCase();
    const result = {level:'insufficient',basis:'none',axes:{},reference:reference?.chart || null,riseMismatch:false,shapeAttention:profile.hipsRoom==='more'||profile.thighRoom==='more',missing:[],calibrated:false,rigid:stretch==='sztywny'||stretch==='rigid'};
    const sourceRise = reference?.row.rise;
    const chartRise = reference?.chart.rise;
    const selectedRise = profile.referenceRise;
    if (actualRise !== null && sourceRise != null && Math.abs(actualRise-sourceRise)>3) result.riseMismatch=true;
    const productRise = product.riseType || 'unknown';
    if (productRise!=='unknown' && selectedRise!=='unknown' && productRise!==selectedRise) result.riseMismatch=true;
    if (productRise!=='unknown' && chartRise && productRise!==chartRise) result.riseMismatch=true;
    const calibration = data.calibration;
    for (const axis of axes) {
      const own = profile.measurements[axis];
      let value = null, kind = null, source = null;
      if (own !== null) { value=own;kind=profile.measurementMode==='body'?'body_measured':'garment_flat';source='customer'; }
      else if (reference?.row[axis] != null) { value=reference.row[axis];kind=reference.chart.kind;source='brand'; }
      if (axis==='inseam' && value===null && profile.leg!==null) {value=profile.leg*2.54;kind='nominal';source='length_label';}
      if (axis==='inseam' && value===null) {
        const heights=data.heightGuide[profile.category].filter(b=>profile.height>=b[0]&&profile.height<=b[1]);
        if (heights.length===1) {value=heights[0][2]*2.54;kind='estimated_length';source='height_guide';}
      }
      const axisResult={actual:actual[axis],reference:value,kind,source,status:'unknown',difference:null};
      result.axes[axis]=axisResult;
      if (actual[axis]===null || value===null) {result.missing.push(axis);continue;}
      if (result.basis==='none' || source==='customer') result.basis=source==='customer'?'measured':kind.startsWith('garment')?'model':'chart';
      const isLength=axis==='inseam';
      if (axis==='waist' && result.riseMismatch && !(source==='customer'&&kind==='body_measured')) {axisResult.status='different_rise';continue;}
      if (axis==='waist' && kind==='body_target') {axisResult.status='body_waist_only';continue;}
      const actualValue = !isLength && (kind==='body_target'||kind==='body_measured'||kind==='garment_circ') ? actual[axis]*2 : actual[axis];
      axisResult.difference=Math.round((actualValue-value)*10)/10;
      if (isLength) {
        axisResult.status=Math.abs(axisResult.difference)<=4?'similar':axisResult.difference<0?'shorter':'longer';
      } else if (kind==='body_target'||kind==='body_measured') {
        const uncertainty=calibration.flatMeasurementToleranceCm*2+(kind==='body_target'?calibration.bodyChartUncertaintyCm:calibration.bodyMeasurementToleranceCm);
        axisResult.status=axisResult.difference < -uncertainty ? 'tight' : axisResult.difference <= uncertainty ? 'check_space' : 'room';
      } else {
        const tolerance=kind==='garment_circ'?calibration.garmentComparisonToleranceCm*2:calibration.garmentComparisonToleranceCm;
        axisResult.status=Math.abs(axisResult.difference)<=tolerance?'similar':axisResult.difference<0?'narrower':'wider';
      }
    }
    const statuses=Object.values(result.axes).map(a=>a.status);
    const compared=['waist','hips','thigh'].map(axis=>result.axes[axis]).filter(a=>!['unknown','body_waist_only','different_rise'].includes(a.status));
    if (statuses.some(s=>s==='tight'||s==='narrower')) result.level='check';
    else if (compared.length) result.level='orientation';
    if (result.axes.thigh.status==='unknown' && profile.thighRoom==='more' && result.level!=='insufficient') result.level='check';
    return result;
  }
  const rules={cm,validAxis,cleanProfile,findReference,compare};
  root.VSJeansFitRules=rules;
  if (typeof module!=='undefined'&&module.exports) module.exports.jeans=rules;
})(typeof window!=='undefined'?window:globalThis);

(function () {
  'use strict';
  if (typeof document==='undefined' || window.VSJeansFitUI) return;
  const rules=window.VSJeansFitRules;
  if (!rules) return;
  const key='vs_jeans_fit_profile_v1';
  const fmt=n=>new Intl.NumberFormat(document.documentElement.lang||'pl',{maximumFractionDigits:1}).format(n);
  function initialize(root) {
    if(root.dataset.vsjReady==='true')return;
    let config;try{config=JSON.parse(root.querySelector('[data-vsj-config]').textContent);}catch(_){return;}
    if(!config.reference?.reviewed||!config.messages||!config.product)return;
    const dialog=root.querySelector('[data-vsj-dialog]'),open=root.querySelector('[data-vsj-open]'),form=root.querySelector('[data-vsj-form]');
    if(!dialog||!open||!form||typeof dialog.showModal!=='function')return;
    root.dataset.vsjReady='true';open.hidden=false;
    const t=(name,values={})=>String(config.messages[name]||name).replace(/\[\[(\w+)\]\]/g,(_,k)=>String(values[k]??''));
    const field=name=>form.elements.namedItem(name);
    const error=root.querySelector('[data-vsj-error]');
    const body=root.querySelector('[data-vsj-body]');
    let step='category',profile=null,previousOverflow='',storageWorks=true;
    const value=name=>field(name)?.value||'';
    const choice=(name,v,label,art)=>{
      const el=document.createElement('label');el.className='vsj-choice';
      const input=document.createElement('input');input.type='radio';input.name=name;input.value=v;input.setAttribute('aria-label',label);el.append(input);
      if(art)el.append(art);
      const text=document.createElement('span');text.textContent=label;el.append(text);return el;
    };
    function clearError(){error.hidden=true;form.querySelectorAll('[aria-invalid]').forEach(el=>el.removeAttribute('aria-invalid'));}
    function fail(name,message){error.textContent=t(message);error.hidden=false;const input=field(name);const target=input?.length&&!input.tagName?input[0]:input;if(target){target.setAttribute('aria-invalid','true');target.focus();}return false;}
    function matchingCharts(){return config.reference.charts.filter(c=>c.brand===value('brand')&&c.category===value('category')&&c.sizeType===value('sizeType'));}
    function selectedChart(){return matchingCharts().find(c=>c.id===value('model'));}
    function flow(){
      const chart=selectedChart(),hasModels=matchingCharts().some(c=>!c.generic);
      return ['category','brand',...(hasModels?['model']:[]),'size','height',...(value('brand')==='levis'?['leg']:[]),...(!chart?.fit?['referenceFit']:[]),...(!chart?.rise?['referenceRise']:[]),'hipsRoom','thighRoom','preference'];
    }
    function updateReference(restore={}) {
      const charts=matchingCharts(),models=charts.filter(c=>!c.generic),model=restore.model??value('model'),size=restore.size??value('size');
      const container=root.querySelector('[data-vsj-models]');container.replaceChildren();
      models.forEach(c=>{
        const img=document.createElement('img');img.src=c.image+(c.image.includes('?')?'&':'?')+'width=400';img.alt='';img.width=200;img.height=240;img.loading='eager';
        const tile=choice('model',c.id,c.label.split(' · ')[0],img);tile.querySelector('input').setAttribute('aria-label',c.label);
        const code=document.createElement('small');code.textContent=c.label.split(' · ').slice(1).join(' · ');tile.append(code);container.append(tile);
      });
      const unknown=choice('model','',t('model_other'));unknown.classList.add('vsj-choice--unknown');const plus=document.createElement('b');plus.textContent='+';plus.setAttribute('aria-hidden','true');unknown.prepend(plus);container.append(unknown);
      const selectedModel=models.some(c=>c.id===model)?model:'';
      form.querySelectorAll('input[name="model"]').forEach(input=>{input.checked=input.value===selectedModel;});
      const chosen=selectedChart()||charts.find(c=>c.generic);
      let sizes=value('sizeType')==='alpha'?['XXS','XS','S','M','L','XL','XXL']:value('sizeType')==='W'?Array.from({length:21},(_,i)=>String(24+i)):Array.from({length:17},(_,i)=>String((value('category')==='women'?30:40)+i*2));
      if(chosen)sizes=chosen.sizes||Object.keys(chosen.rows);
      const picker=root.querySelector('[data-vsj-sizes]');picker.replaceChildren();sizes.forEach(v=>picker.append(choice('size',v,v)));
      if(sizes.includes(size))field('size').value=size;
      const note=root.querySelector('[data-vsj-reference-note]');note.textContent=chosen?(chosen.kind==='body_target'?t('body_table_note'):chosen.rows[size]||!size?t('model_table_note'):t('model_size_missing')):t('need_model_note');
      return chosen;
    }
    function draft(){return{category:field('category').value,brand:field('brand').value,sizeType:field('sizeType').value,size:field('size').value,model:field('model').value,height:field('height').value,leg:value('brand')==='levis'?value('leg'):'',referenceFit:value('referenceFit')||'unknown',referenceRise:value('referenceRise')||'unknown',hipsRoom:field('hipsRoom').value,thighRoom:field('thighRoom').value,preference:field('preference').value,measurementMode:field('measurementMode').value,measurements:Object.fromEntries(['waist','hips','thigh','inseam'].map(a=>[a,field('measure_'+a).value]))};}
    function fill(p){
      for(const name of ['category','brand','sizeType'])field(name).value=p[name];
      updateReference(p);
      for(const name of ['height','leg','referenceFit','referenceRise','hipsRoom','thighRoom','preference','measurementMode'])field(name).value=p[name]??'';
      for(const axis of ['waist','hips','thigh','inseam'])field('measure_'+axis).value=p.measurements?.[axis]??'';
      measureLabels();
    }
    function persist(p){profile=p;try{sessionStorage.setItem(key,JSON.stringify({version:1,dataVersion:config.reference.version,profile:p}));}catch(_){storageWorks=false;}}
    function move(n,focus=true){
      step=n;clearError();const screens=flow(),result=n==='result',index=screens.indexOf(n);
      root.querySelectorAll('[data-vsj-step]').forEach(el=>{el.hidden=el.dataset.vsjStep!==n;});
      root.querySelector('[data-vsj-result]').hidden=!result;
      root.querySelector('[data-vsj-back]').hidden=index===0;
      root.querySelector('[data-vsj-back]').textContent=t(result?'edit':'back');
      root.querySelector('[data-vsj-next]').textContent=t(result?'done':index===screens.length-1?'show_result':'next');
      root.querySelector('[data-vsj-progress-label]').textContent=result?t('your_result'):t('step',{n:index+1,total:screens.length});
      const progress={category:7,brand:17,model:27,size:38,height:49,leg:55,referenceFit:60,referenceRise:65,hipsRoom:73,thighRoom:82,preference:92,result:100};
      root.querySelector('[data-vsj-progress-bar]').style.width=progress[n]+'%';
      body.scrollTop=0;
      if(focus){const heading=dialog.querySelector(result?'[data-vsj-result] h2':'[data-vsj-step="'+n+'"] h2');heading?.focus({preventScroll:true});}
    }
    function validateStep(){
      clearError();
      if(step==='brand'&&!value('brand'))return fail('brand','brand_error');
      if(step==='size'&&!value('size'))return fail('size','size_error');
      if(step==='height'){const h=rules.cm(value('height'));if(h===null||h<130||h>220)return fail('height','height_error');}
      if(['category','referenceFit','referenceRise','hipsRoom','thighRoom','preference'].includes(step)&&!value(step))return fail(step,'answer_error');
      return true;
    }
    function measureLabels(){
      const mode=field('measurementMode').value;
      root.querySelector('[data-vsj-measure-help]').textContent=t(mode==='body'?'body_help':'garment_help');
      for(const axis of ['waist','hips','thigh','inseam'])root.querySelector('[data-vsj-measure-label="'+axis+'"]').textContent=t((mode==='body'?'body_':'flat_')+axis);
    }
    function axisView(axis,a){
      let label=t('status_'+a.status),detail='';
      if(a.actual===null){label=t('missing_product');detail=t('missing_'+axis);}
      else if(a.reference===null){label=t('add_reference');detail=t('actual_cm',{n:fmt(a.actual)});}
      else if(a.status==='different_rise'){detail=t('rise_explanation');}
      else if(a.status==='body_waist_only'){detail=t('waist_explanation');}
      else if(a.kind==='body_target'||a.kind==='body_measured'){
        detail=t(a.kind==='body_target'?'body_chart_comparison':'body_comparison',{actual:fmt(a.actual*2),ref:fmt(a.reference)});
      }else{
        const actual=a.kind==='garment_circ'&&axis!=='inseam'?a.actual*2:a.actual;
        detail=t(a.kind==='estimated_length'?'estimated_comparison':a.kind==='nominal'?'nominal_comparison':'garment_comparison',{actual:fmt(actual),ref:fmt(a.reference)});
      }
      return{label,detail};
    }
    function renderResult(p){
      const result=rules.compare(config.product,p,config.reference);
      const core=['waist','hips','thigh'].filter(a=>!['unknown','different_rise','body_waist_only'].includes(result.axes[a].status));
      const tight=['waist','hips','thigh'].find(a=>['tight','narrower'].includes(result.axes[a].status));
      let title='headline_orientation',intro='intro_orientation';
      if(!core.length){title='headline_missing';intro='intro_missing';}
      else if(tight){title='headline_check';intro='intro_check';}
      else if(p.thighRoom==='more'&&result.axes.thigh.status==='unknown'){title='headline_thigh';intro='intro_thigh';}
      else if(result.riseMismatch){title='headline_rise';intro='intro_rise';}
      else if(result.rigid&&core.some(a=>result.axes[a].status==='check_space')){title='headline_rigid';intro='intro_rigid';}
      else if(p.preference==='fitted'&&core.some(a=>['wider','room'].includes(result.axes[a].status))){title='headline_room';intro='intro_room';}
      root.querySelector('[data-vsj-result-title]').textContent=t(title);
      root.querySelector('[data-vsj-result-intro]').textContent=t(intro,tight?{axis:t('axis_'+tight)}:{});
      root.querySelector('[data-vsj-basis]').textContent=t(result.basis==='measured'?'basis_measured':result.basis==='model'?'basis_model':'basis_orientation');
      for(const axis of ['waist','hips','thigh','inseam']){
        const a=result.axes[axis],card=root.querySelector('[data-vsj-axis="'+axis+'"]'),view=axisView(axis,a);
        card.querySelector('b').textContent=view.label;card.querySelector('small').textContent=view.detail;
        card.dataset.status=['tight','narrower','check_space','different_rise'].includes(a.status)?'warning':['unknown','body_waist_only'].includes(a.status)?'unknown':'neutral';
      }
      root.querySelector('[data-vsj-preference-note]').textContent=t('preference_note',{fit:t('preference_'+p.preference),cut:config.product.fit||t('unknown_cut')});
      const stretch=String(config.product.stretch||'').trim();
      root.querySelector('[data-vsj-stretch-note]').textContent=t(!stretch||/^(nieznana|unknown)$/i.test(stretch)?'stretch_unknown':'stretch_known',{stretch});
      const sources=root.querySelector('[data-vsj-sources]');sources.replaceChildren();
      const addSource=(label,url)=>{const li=document.createElement('li');if(url&&/^https:\/\/(www\.)?(zara\.com|levi\.com|hm\.com)\//.test(url)||url&&/^https:\/\/www2\.hm\.com\//.test(url)){const a=document.createElement('a');a.textContent=label;a.href=url;a.target='_blank';a.rel='noopener noreferrer';li.append(a);}else li.textContent=label;sources.append(li);};
      if(result.reference)addSource(result.reference.label+' · '+p.sizeType+' '+p.size,result.reference.source);
      if(Object.values(p.measurements).some(v=>v!==null))addSource(t('source_customer'));
      if(result.axes.inseam.source==='height_guide')addSource(t('source_height'),config.reference.heightGuide.source);
      if(!sources.children.length)addSource(t('source_missing'));
      root.querySelector('[data-vsj-memory]').textContent=t(storageWorks?'memory':'memory_page');
      root.querySelector('[data-vsj-saved]').textContent=t(title);root.querySelector('[data-vsj-saved]').hidden=false;
      root.querySelector('[data-vsj-open-label]').textContent=t('edit_fit');
      return result;
    }
    function calculate(){
      clearError();let p=rules.cleanProfile(draft());
      if(!p){for(const axis of ['waist','hips','thigh','inseam']){const input=field('measure_'+axis);if(input.value.trim()!==''&&rules.validAxis(input.value,axis,field('measurementMode').value)===null){root.querySelector('[data-vsj-refine-details]').open=true;return fail('measure_'+axis,'measurement_error');}}return fail('height','profile_error');}
      persist(p);renderResult(p);move('result');return true;
    }
    root.querySelector('[data-vsj-next]').addEventListener('click',()=>{
      if(step==='result'){dialog.close();return;}
      if(!validateStep())return;
      const screens=flow(),index=screens.indexOf(step);
      if(index===screens.length-1)calculate();else move(screens[index+1]);
    });
    root.querySelector('[data-vsj-back]').addEventListener('click',()=>{const screens=flow();move(step==='result'?'category':screens[Math.max(0,screens.indexOf(step)-1)]);});
    root.querySelector('[data-vsj-close]').addEventListener('click',()=>dialog.close());
    form.addEventListener('submit',event=>{event.preventDefault();root.querySelector('[data-vsj-next]').click();});
    form.addEventListener('change',event=>{
      clearError();const name=event.target.name;
      if(name==='brand'){
        field('sizeType').value=value('brand')==='levis'?'W':'EU';field('referenceFit').value='';field('referenceRise').value='';updateReference({model:'',size:''});
      } else if(name==='category'||name==='sizeType'){
        field('referenceFit').value='';field('referenceRise').value='';updateReference({model:'',size:''});
      } else if(name==='model'){
        const chart=updateReference({model:value('model'),size:value('size')});
        field('referenceRise').value=chart?.rise||'';field('referenceFit').value=chart?.fit||'';
      } else if(name==='size'){
        const chosen=selectedChart()||matchingCharts().find(c=>c.generic);
        root.querySelector('[data-vsj-reference-note]').textContent=chosen?(chosen.kind==='body_target'?t('body_table_note'):chosen.rows[value('size')]?t('model_table_note'):t('model_size_missing')):t('need_model_note');
      } else if(name==='measurementMode')measureLabels();
      if(step!=='result')move(step,false);
    });
    root.querySelector('[data-vsj-refine]').addEventListener('click',()=>calculate());
    root.querySelector('[data-vsj-clear]').addEventListener('click',()=>{try{sessionStorage.removeItem(key);}catch(_){}profile=null;form.reset();updateReference();measureLabels();root.querySelector('[data-vsj-saved]').hidden=true;root.querySelector('[data-vsj-open-label]').textContent=t('open');move('category');});
    open.addEventListener('click',()=>{if(profile){fill(profile);renderResult(profile);move('result',false);}else move('category',false);previousOverflow=document.documentElement.style.overflow;document.documentElement.style.overflow='hidden';dialog.showModal();open.setAttribute('aria-expanded','true');});
    dialog.addEventListener('close',()=>{document.documentElement.style.overflow=previousOverflow;open.setAttribute('aria-expanded','false');open.focus({preventScroll:true});});
    dialog.addEventListener('click',event=>{if(event.target!==dialog)return;const r=dialog.getBoundingClientRect();if(event.clientX<r.left||event.clientX>r.right||event.clientY<r.top||event.clientY>r.bottom)dialog.close();});
    updateReference();measureLabels();
    try{const saved=JSON.parse(sessionStorage.getItem(key));const p=saved?.version===1?rules.cleanProfile(saved.profile):null;if(p){profile=p;fill(p);renderResult(p);}}catch(_){storageWorks=false;}
  }
  window.VSJeansFitUI={initialize};
  const all=scope=>scope.querySelectorAll('[data-vsj-widget]').forEach(initialize);
  all(document);document.addEventListener('shopify:section:load',event=>all(event.target));
})();
